import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, FileText, RefreshCw, Table, Image as ImageIcon, AlertTriangle, Globe, Code } from 'lucide-react';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import { ApiClient } from '../../api/client';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

interface UniversalFilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  fileUrl: string;
  mimeType?: string;
  onReplace?: () => void;
}

export const UniversalFilePreviewModal: React.FC<UniversalFilePreviewModalProps> = ({
  isOpen,
  onClose,
  fileName,
  fileUrl,
  mimeType,
  onReplace,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Spreadsheet state
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [sheetData, setSheetData] = useState<any[][]>([]);

  // Word (DOCX) state
  const [docxHtml, setDocxHtml] = useState<string | null>(null);

  // Text state
  const [textContent, setTextContent] = useState<string | null>(null);

  // HTML / Nem valódi PDF (bejelentkező oldal) állapot
  const [isDetectedHtml, setIsDetectedHtml] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [htmlViewMode, setHtmlViewMode] = useState<'render' | 'source'>('render');

  // Kinyert HTML metaadatok és stílus-felkészítés a láthatósághoz
  const htmlInfo = useMemo(() => {
    if (!htmlContent) return null;
    const titleMatch = htmlContent.match(/<title[^>]*>([^<]+)<\/title>/i);
    const title = titleMatch ? titleMatch[1].trim() : '';

    const cleanText = htmlContent
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    let platform = 'Weboldal / Bejelentkező felület';
    if (/google/i.test(htmlContent) || /drive/i.test(htmlContent)) {
      platform = 'Google Drive / Google Fiók belépés';
    } else if (/kreta|kréta|eduid/i.test(htmlContent)) {
      platform = 'Kréta / Intézményi belépőkapu';
    } else if (/microsoft|office|azure|live\.com/i.test(htmlContent)) {
      platform = 'Microsoft 365 / Office belépőfelület';
    } else if (/404|not found|nem található/i.test(cleanText)) {
      platform = '404 - Nem található oldal';
    }

    // Beszúrunk egy felülbíráló CSS-t, ami biztosítja a Google / JS által elrejtett elemek azonnali kirajzolását
    const visibilityCss = `
<style id="kozad-preview-visibility-fix">
  html, body {
    width: 100% !important;
    min-height: 100% !important;
    background-color: #ffffff !important;
    color: #1f2937 !important;
    margin: 0 !important;
    padding: 20px !important;
    box-sizing: border-box !important;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
  }
  * { visibility: visible !important; opacity: 1 !important; }
  [hidden] { display: block !important; }
  form { display: block !important; margin: 16px 0 !important; }
  input, button, select, textarea { display: inline-block !important; }
  div, p, h1, h2, h3, h4, h5, h6, span { visibility: visible !important; }
</style>
`;
    let prepared = htmlContent;
    if (prepared.includes('</head>')) {
      prepared = prepared.replace('</head>', `${visibilityCss}</head>`);
    } else if (prepared.includes('<body')) {
      prepared = prepared.replace('<body', `<head>${visibilityCss}</head><body`);
    } else {
      prepared = `${visibilityCss}${prepared}`;
    }

    return {
      title,
      platform,
      snippet: cleanText.slice(0, 240),
      preparedHtml: prepared,
    };
  }, [htmlContent]);

  // Blob URL for secure authenticated iframe / image preview
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const ext = (fileName.split('.').pop() || '').toLowerCase();
  const isHtml = ['html', 'htm'].includes(ext) || mimeType?.toLowerCase().includes('text/html');
  const isDocx = ['docx', 'doc'].includes(ext);
  const isSpreadsheet = ['xlsx', 'xls', 'csv', 'ods'].includes(ext) || (!isDocx && !isHtml && (mimeType?.includes('spreadsheet') || mimeType?.includes('csv')));
  const isImage = ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(ext) || (!isDocx && !isHtml && mimeType?.startsWith('image/'));
  const isPdf = !isHtml && (ext === 'pdf' || (!isDocx && !isSpreadsheet && !isImage && mimeType?.includes('pdf')));
  const isText = !isHtml && (['txt', 'json', 'xml', 'md', 'log'].includes(ext) || (!isDocx && mimeType?.includes('text')));

  const token = ApiClient.getToken();
  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const authUrl = token && fileUrl.startsWith('/api/') && !fileUrl.includes('token=')
    ? `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
    : fileUrl;

  const previewFetchUrl = fileUrl
    ? `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}preview=1`
    : '';

  useEffect(() => {
    if (!isOpen || !fileUrl) return;
    setLoading(true);
    setError(null);
    setSheetNames([]);
    setSheetData([]);
    setTextContent(null);
    setDocxHtml(null);
    setIsDetectedHtml(false);
    setHtmlContent(null);
    setHtmlViewMode('render');
    setBlobUrl(null);

    let activeBlobUrl: string | null = null;
    let isCancelled = false;

    const loadContent = async () => {
      try {
        if (isSpreadsheet) {
          const res = await fetch(previewFetchUrl, { headers: authHeaders });
          if (!res.ok) {
            const errJson = await res.json().catch(() => null);
            throw new Error(errJson?.error || `Hiba a táblázat betöltésekor (${res.status})`);
          }
          const buf = await res.arrayBuffer();
          if (isCancelled) return;
          const wb = XLSX.read(buf, { type: 'array', cellFormula: false, cellHTML: false });
          setSheetNames(wb.SheetNames);
          if (wb.SheetNames.length > 0) {
            const firstSheet = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
            setSheetData(json.slice(0, 100));
          }
          setLoading(false);
        } else if (isDocx) {
          const res = await fetch(previewFetchUrl, { headers: authHeaders });
          if (!res.ok) {
            const errJson = await res.json().catch(() => null);
            throw new Error(errJson?.error || `Hiba a Word dokumentum betöltésekor (${res.status})`);
          }
          const buf = await res.arrayBuffer();
          if (isCancelled) return;
          try {
            const result = await mammoth.convertToHtml({ arrayBuffer: buf });
            if (result.value && result.value.trim().length > 0) {
              setDocxHtml(result.value);
            } else {
              const dec = new TextDecoder('utf-8');
              const txt = dec.decode(buf);
              setTextContent(txt);
            }
          } catch {
            const dec = new TextDecoder('utf-8');
            const txt = dec.decode(buf);
            if (txt.includes('%PDF-') || txt.length < 2000) {
              setTextContent(txt);
            } else {
              throw new Error('A Word dokumentum közvetlen megjelenítése nem támogatott vagy a fájl sérült.');
            }
          }
          setLoading(false);
        } else if (isPdf || isHtml) {
          const res = await fetch(previewFetchUrl, { headers: authHeaders });
          if (!res.ok) {
            const errJson = await res.json().catch(() => null);
            throw new Error(errJson?.error || `Hiba a fájl betöltésekor (${res.status})`);
          }
          const blob = await res.blob();
          if (isCancelled) return;

          // Ellenőrizzük az első 4096 bájtot, hogy valóban PDF vagy HTML oldal
          const headSlice = blob.slice(0, 4096);
          const headText = await headSlice.text();
          const lowerHead = headText.toLowerCase();

          const isActuallyHtml =
            isHtml ||
            mimeType?.toLowerCase().includes('text/html') ||
            blob.type.toLowerCase().includes('text/html') ||
            (!headText.includes('%PDF-') && (
              lowerHead.includes('<!doctype') ||
              lowerHead.includes('<html') ||
              lowerHead.includes('<body') ||
              lowerHead.includes('<head') ||
              lowerHead.includes('<script') ||
              lowerHead.includes('<form') ||
              lowerHead.includes('<meta') ||
              lowerHead.includes('bejelentkezés') ||
              lowerHead.includes('felhasználónév') ||
              lowerHead.includes('jelszó')
            ));

          if (isActuallyHtml) {
            setIsDetectedHtml(true);
            const fullText = await blob.text();
            setHtmlContent(fullText);
            const htmlBlob = new Blob([blob], { type: 'text/html;charset=utf-8' });
            activeBlobUrl = URL.createObjectURL(htmlBlob);
            setBlobUrl(activeBlobUrl);
            setLoading(false);
            return;
          }

          const pdfBlob = new Blob([blob], { type: 'application/pdf' });
          activeBlobUrl = URL.createObjectURL(pdfBlob);
          setBlobUrl(activeBlobUrl);
          setLoading(false);
        } else if (isImage) {
          const res = await fetch(previewFetchUrl, { headers: authHeaders });
          if (!res.ok) {
            const errJson = await res.json().catch(() => null);
            throw new Error(errJson?.error || `Hiba a kép betöltésekor (${res.status})`);
          }
          const blob = await res.blob();
          if (isCancelled) return;
          activeBlobUrl = URL.createObjectURL(blob);
          setBlobUrl(activeBlobUrl);
          setLoading(false);
        } else if (isText) {
          const res = await fetch(previewFetchUrl, { headers: authHeaders });
          if (!res.ok) {
            const errJson = await res.json().catch(() => null);
            throw new Error(errJson?.error || `Hiba a szöveg betöltésekor (${res.status})`);
          }
          const text = await res.text();
          if (isCancelled) return;
          setTextContent(text);
          setLoading(false);
        } else {
          setLoading(false);
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err.message || 'Nem sikerült betölteni az előnézetet');
          setLoading(false);
        }
      }
    };

    loadContent();

    return () => {
      isCancelled = true;
      if (activeBlobUrl) {
        URL.revokeObjectURL(activeBlobUrl);
      }
    };
  }, [isOpen, fileUrl, ext, isSpreadsheet, isDocx, isPdf, isImage, isText, isHtml]);

  const handleSheetChange = async (idx: number) => {
    setActiveSheetIndex(idx);
    setLoading(true);
    try {
      const res = await fetch(fileUrl, { headers: authHeaders });
      const buf = await res.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[idx]];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
      setSheetData(json.slice(0, 100));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/75 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-5xl h-[88vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Fejléc */}
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/80 dark:bg-slate-950/80 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            {isDetectedHtml ? (
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 animate-pulse" />
            ) : isPdf ? (
              <FileText className="w-5 h-5 text-rose-500 shrink-0" />
            ) : isDocx ? (
              <FileText className="w-5 h-5 text-blue-600 shrink-0" />
            ) : isImage ? (
              <ImageIcon className="w-5 h-5 text-indigo-500 shrink-0" />
            ) : isSpreadsheet ? (
              <Table className="w-5 h-5 text-emerald-500 shrink-0" />
            ) : (
              <FileText className="w-5 h-5 text-slate-500 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate" title={fileName}>
                  {fileName}
                </h3>
                {isDetectedHtml && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 shrink-0">
                    Mentett weboldal (HTML)
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {isDetectedHtml
                  ? 'Weboldal / HTML bejelentkező felület (Nem PDF)'
                  : isDocx
                  ? `Microsoft Word dokumentum (.${ext})`
                  : isPdf
                  ? 'PDF dokumentum'
                  : isImage
                  ? `Kép fájl (.${ext})`
                  : isSpreadsheet
                  ? `Munkafüzet / Táblázat (.${ext})`
                  : `Fájl előnézet (.${ext})`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onReplace && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onReplace();
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-semibold shadow-xs transition cursor-pointer"
                title="Fájl cseréje valódi dokumentumra"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Csere (Új verzió)</span>
              </button>
            )}
            <a
              href={authUrl}
              download={fileName}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:hover:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 rounded-lg text-xs font-semibold transition cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Letöltés</span>
            </a>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Figyelmeztető sáv, ha a fájl nem igazi PDF, hanem mentett HTML / bejelentkező oldal */}
        {isDetectedHtml && (
          <div className="px-4 py-3 bg-amber-50 dark:bg-amber-950/50 border-b border-amber-200 dark:border-amber-800/80 flex items-start gap-3 text-xs shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 space-y-1">
              <div className="font-bold text-amber-900 dark:text-amber-200">
                ⚠️ Ez a fájl nem PDF dokumentum, hanem egy mentett weboldal (HTML kód / bejelentkező felület)!
              </div>
              <p className="text-amber-800/90 dark:text-amber-300/90 leading-relaxed text-[11px]">
                A fájl neve <strong>„{fileName}”</strong>, azonban a korábbi letöltéskor az intézményi tárhelyen jelszavas vagy bejelentkezést igénylő védelem volt érvényben, így a dokumentum helyett az intézmény belépési űrlapja mentődött le.
              </p>
              <div className="pt-0.5 flex flex-wrap items-center gap-3 text-[11px]">
                <span className="font-semibold text-amber-950 dark:text-amber-100">
                  💡 Teendő: Ellenőrizze az alábbi felületet, keresse elő az eredeti PDF-et, és töltse fel a <strong>Csere (🔄)</strong> gombbal.
                </span>
                {onReplace && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onReplace();
                    }}
                    className="font-bold text-amber-900 dark:text-amber-200 underline hover:no-underline cursor-pointer"
                  >
                    Fájl cseréje most →
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Fülváltó HTML nézethez */}
        {isDetectedHtml && (
          <div className="px-4 py-2 bg-slate-100 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setHtmlViewMode('render')}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1.5 ${
                  htmlViewMode === 'render'
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200'
                }`}
              >
                <Globe className="w-3.5 h-3.5" />
                <span>Mentett weboldal előnézete</span>
              </button>
              <button
                type="button"
                onClick={() => setHtmlViewMode('source')}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1.5 ${
                  htmlViewMode === 'source'
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200'
                }`}
              >
                <Code className="w-3.5 h-3.5" />
                <span>Nyers forráskód (HTML)</span>
              </button>
            </div>
            <span className="text-[11px] text-slate-500 dark:text-slate-400 hidden sm:inline">
              Biztonságos izolált nézet
            </span>
          </div>
        )}

        {/* Fülváltó táblázatokhoz */}
        {isSpreadsheet && sheetNames.length > 1 && (
          <div className="px-4 py-2 bg-slate-100 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2 overflow-x-auto shrink-0">
            {sheetNames.map((name: string, idx: number) => (
              <button
                key={name}
                onClick={() => handleSheetChange(idx)}
                className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition cursor-pointer ${
                  activeSheetIndex === idx
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Tartalmi Előnézet Konténer */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-slate-100/50 dark:bg-slate-950/50">
          {loading ? (
            <div className="text-center space-y-3">
              <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400 mx-auto" />
              <p className="text-sm text-slate-500 dark:text-slate-400">Előnézet betöltése folyamatban...</p>
            </div>
          ) : error ? (
            <div className="text-center max-w-md p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm space-y-3">
              <p className="text-sm text-rose-600 dark:text-rose-400 font-semibold">{error}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                A fájl ettől függetlenül közvetlenül letölthető és megnyitható az eszközén.
              </p>
              <a
                href={authUrl}
                download={fileName}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold shadow-xs transition"
              >
                <Download className="w-4 h-4" />
                <span>Letöltés most</span>
              </a>
            </div>
          ) : isDetectedHtml ? (
            htmlViewMode === 'render' ? (
              <div className="w-full h-full flex flex-col rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xs bg-white">
                {/* Fejléc / infó kártya az észlelt oldalról */}
                {htmlInfo && (
                  <div className="px-4 py-2 bg-slate-100 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs text-slate-700 dark:text-slate-300 shrink-0">
                    <div className="flex items-center gap-2 truncate">
                      <span className="font-bold text-amber-800 dark:text-amber-300 bg-amber-100 dark:bg-amber-950 border border-amber-300 dark:border-amber-700 px-2 py-0.5 rounded text-[10px] shrink-0">
                        {htmlInfo.platform}
                      </span>
                      {htmlInfo.title && (
                        <span className="font-semibold truncate text-slate-900 dark:text-white" title={htmlInfo.title}>
                          „{htmlInfo.title}”
                        </span>
                      )}
                    </div>
                    {htmlInfo.snippet && (
                      <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-md hidden md:inline" title={htmlInfo.snippet}>
                        Kivonat: {htmlInfo.snippet}
                      </span>
                    )}
                  </div>
                )}
                <iframe
                  srcDoc={htmlInfo?.preparedHtml || htmlContent || undefined}
                  className="w-full flex-1 border-0 bg-white"
                  // S6: üres sandbox = nincs szkript, nincs űrlapküldés, és a keret egyedi
                  // (opaque) originen fut, így nem éri el az alkalmazás localStorage-át.
                  sandbox=""
                  referrerPolicy="no-referrer"
                  title={fileName}
                />
              </div>
            ) : (
              <div className="w-full h-full overflow-auto bg-slate-950 rounded-xl border border-slate-800 p-4 select-text">
                <pre className="font-mono text-xs text-amber-200/90 whitespace-pre-wrap leading-relaxed">
                  {htmlContent}
                </pre>
              </div>
            )
          ) : isPdf ? (
            <iframe
              src={blobUrl || authUrl}
              className="w-full h-full rounded-xl border border-slate-200 dark:border-slate-800 bg-white"
              title={fileName}
            />
          ) : isDocx && docxHtml ? (
            <div className="w-full h-full overflow-auto bg-slate-100 dark:bg-slate-950 p-4 sm:p-8 flex justify-center">
              <div className="w-full max-w-4xl bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 p-8 sm:p-14 text-slate-800 dark:text-slate-100 leading-relaxed font-sans text-sm">
                <div className="pb-4 mb-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-400 font-mono">
                  <span>DOKUMENTUM TARTALOM (DOCX)</span>
                  <span className="truncate max-w-xs">{fileName}</span>
                </div>
                <div
                  className="docx-preview-content prose dark:prose-invert max-w-none [&_table]:w-full [&_table]:border-collapse [&_table]:my-6 [&_table]:border [&_table]:border-slate-300 dark:[&_table]:border-slate-700 [&_table]:shadow-2xs [&_td]:border [&_td]:border-slate-300 dark:[&_td]:border-slate-700 [&_td]:p-3 [&_td]:align-top [&_th]:border [&_th]:border-slate-300 dark:[&_th]:border-slate-700 [&_th]:p-3 [&_th]:bg-slate-50 dark:[&_th]:bg-slate-800 [&_th]:font-bold [&_p]:mb-3.5 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-3 [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-2"
                  // S6: a mammoth kimenete engedélyezőlistás szanitáláson megy át (javascript: linkek,
                  // eseménykezelők, beágyazott script/iframe eltávolítva).
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(docxHtml) }}
                />
              </div>
            </div>
          ) : isImage ? (
            <div className="max-w-full max-h-full flex items-center justify-center p-2">
              <img
                src={blobUrl || authUrl}
                alt={fileName}
                className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-lg border border-slate-200 dark:border-slate-800"
              />
            </div>
          ) : isSpreadsheet ? (
            <div className="w-full h-full overflow-auto bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-2">
              {sheetData.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">Üres munkalap</div>
              ) : (
                <table className="w-full text-xs text-left border-collapse">
                  <tbody>
                    {sheetData.map((row: any[], rIdx: number) => (
                      <tr key={rIdx} className={rIdx === 0 ? 'bg-slate-100 dark:bg-slate-800 font-bold' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800/60'}>
                        {row.map((cell: any, cIdx: number) => (
                          <td key={cIdx} className="px-3 py-2 border border-slate-200 dark:border-slate-800 truncate max-w-xs">
                            {cell !== undefined && cell !== null ? String(cell) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (textContent || isText) ? (
            <div className="w-full h-full overflow-auto bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
              <pre className="font-mono text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                {textContent}
              </pre>
            </div>
          ) : (
            <div className="text-center max-w-md p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm space-y-3">
              <FileText className="w-12 h-12 text-indigo-500 mx-auto" />
              <h4 className="text-sm font-bold text-slate-900 dark:text-white">Közvetlen előnézet nem érhető el</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                A(z) .{ext} kiterjesztésű fájlok megtekintéséhez kérjük, töltse le a dokumentumot.
              </p>
              <a
                href={authUrl}
                download={fileName}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold"
              >
                <Download className="w-4 h-4" />
                <span>Fájl Letöltése</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
